import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { reportService } from '../../services/reportService';
import { 
  ReportType, 
  ReportCategory, 
  REPORT_CATEGORY_INFO 
} from '../../types/reporting';
import { LoadingSpinner } from '../ui';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetType: ReportType;
  targetId: string;
  targetTitle: string;
}

export function ReportModal({
  isOpen,
  onClose,
  targetType,
  targetId,
  targetTitle
}: ReportModalProps) {
  const { user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState<ReportCategory | ''>('');
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [evidenceFiles, setEvidenceFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length + evidenceFiles.length > 3) {
      alert('Maximum 3 files allowed');
      return;
    }
    setEvidenceFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index: number) => {
    setEvidenceFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !selectedCategory || !reason.trim()) {
      setSubmitError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await reportService.submitReport({
        type: targetType,
        targetId,
        category: selectedCategory,
        reason: reason.trim(),
        description: description.trim() || undefined,
        evidence: evidenceFiles.length > 0 ? evidenceFiles : undefined
      }, user.uid);

      setSubmitSuccess(true);
      
      // Reset form
      setTimeout(() => {
        onClose();
        setSubmitSuccess(false);
        setSelectedCategory('');
        setReason('');
        setDescription('');
        setEvidenceFiles([]);
      }, 2000);

    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to submit report');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRelevantCategories = (): ReportCategory[] => {
    if (targetType === 'event') {
      return [
        ReportCategory.FAKE_EVENT,
        ReportCategory.DANGEROUS_EVENT,
        ReportCategory.PRICE_FRAUD,
        ReportCategory.LOCATION_FRAUD,
        ReportCategory.SPAM,
        ReportCategory.INAPPROPRIATE_CONTENT,
        ReportCategory.MISLEADING_INFO,
        ReportCategory.COPYRIGHT,
        ReportCategory.OTHER
      ];
    } else {
      return [
        ReportCategory.FAKE_PROFILE,
        ReportCategory.IMPERSONATION,
        ReportCategory.SCAMMER,
        ReportCategory.HARASSMENT,
        ReportCategory.HATE_SPEECH,
        ReportCategory.VIOLENCE,
        ReportCategory.DOXXING,
        ReportCategory.SPAM,
        ReportCategory.OTHER
      ];
    }
  };

  if (submitSuccess) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Report Submitted</h3>
          <p className="text-gray-600">Thank you for helping keep our community safe. We'll review this report promptly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Report {targetType === 'event' ? 'Event' : 'User'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Reporting: <span className="font-medium">{targetTitle}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Category Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              What's the issue? <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 gap-3">
              {getRelevantCategories().map((category) => (
                <label
                  key={category}
                  className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedCategory === category
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="category"
                    value={category}
                    checked={selectedCategory === category}
                    onChange={(e) => setSelectedCategory(e.target.value as ReportCategory)}
                    className="mt-1 mr-3 text-primary-600"
                  />
                  <div>
                    <div className="font-medium text-gray-900">
                      {REPORT_CATEGORY_INFO[category].title}
                    </div>
                    <div className="text-sm text-gray-600">
                      {REPORT_CATEGORY_INFO[category].description}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Reason */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Please provide a brief reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Brief summary of the issue..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              maxLength={100}
              required
            />
            <div className="text-xs text-gray-500 mt-1">
              {reason.length}/100 characters
            </div>
          </div>

          {/* Description */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional details (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide additional context or details..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              maxLength={500}
            />
            <div className="text-xs text-gray-500 mt-1">
              {description.length}/500 characters
            </div>
          </div>

          {/* Evidence Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Evidence (optional)
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                className="hidden"
                id="evidence-upload"
              />
              <label
                htmlFor="evidence-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span className="text-sm text-gray-600">Click to upload images (max 3)</span>
              </label>
            </div>

            {/* File List */}
            {evidenceFiles.length > 0 && (
              <div className="mt-3 space-y-2">
                {evidenceFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                    <span className="text-sm text-gray-700 truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700 ml-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Error Message */}
          {submitError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {submitError}
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedCategory || !reason.trim() || isSubmitting}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <LoadingSpinner size="sm" />
                  Submitting...
                </>
              ) : (
                'Submit Report'
              )}
            </button>
          </div>

          {/* Disclaimer */}
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-xs text-yellow-800">
              <strong>Important:</strong> False reports may result in action against your account. 
              Only report content that genuinely violates our community guidelines.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}